import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Info, ArrowLeft, Search } from 'lucide-react';
import { api, calculerMontantsAvecReduction, calculerEconomieAnnuelle, formaterMontant } from '../services/api';
import { Toast } from '../components/ui/Toast';
import CarteChoix from '../components/ui/CarteChoix';
import { useFeatures } from '../contexts/FeaturesContext';

const FamilleNombreuseStep = ({ familleInfo, setFamilleInfo, onValidate, classeSelectionnee }) => {
  // Chaque type de reduction porte sur les frais scolaires : sans module
  // financier, ce ne serait qu une note sans effet, qu il faudrait ensuite
  // croire sur parole. Les colonnes restent en base et se remplissent depuis
  // la comptabilite le jour ou l ecole y accede.
  const { estActive } = useFeatures();
  const famillesOuvertes = estActive('module.familles');
  const reductionsFinancieres = estActive('module.comptabilite');
  const auMoinsUneReduction = famillesOuvertes || reductionsFinancieres;

  const [typeReduction, setTypeReduction] = useState(
    familleInfo.appartient_famille ? 'famille' :
      familleInfo.reduction_exceptionnelle ? 'exceptionnelle' :
        familleInfo.politique_ecole ? 'politique' :
          null
  );

  const [mode, setMode] = useState('');
  const [familles, setFamilles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toastInfo, setToastInfo] = useState(null);

  // 🆕 NOUVEAU : Politique d'école
  // La reduction mensuelle n'est reprise que si la politique etait deja choisie :
  // sinon ce serait celle d'un autre mode (famille, exceptionnelle), recopiee
  // dans l'inscription des qu'on choisit une categorie, et envoyee au serveur.
  const [politiqueEcole, setPolitiqueEcole] = useState({
    type: familleInfo.politique_type || null,
    reduction_mensuel: familleInfo.politique_ecole ? (familleInfo.reduction_mensuel || '') : '',
    motif: familleInfo.politique_motif || ''
  });


  // Formulaire création famille
  const [nouvelleFamille, setNouvelleFamille] = useState({
    nom: '',
    reduction_inscription: '',
    reduction_mensuel: '',
    motif: '',
    // Décochée par défaut : dans cette école, chaque enfant a sa propre réduction.
    // Cocher fait de la réduction de cet élève la valeur que les prochains membres
    // pourront reprendre.
    valeur_de_famille: false,
    description: ''
  });

  // Famille sélectionnée
  const [familleSelectionnee, setFamilleSelectionnee] = useState(null);




  // ✅ NOUVEAUX ÉTATS pour la recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [totalFamilles, setTotalFamilles] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredFamilles, setFilteredFamilles] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  // 🆕 Choix de règles pour famille existante
  const [choixReglesFamille, setChoixReglesFamille] = useState(null); // 'meme' ou 'custom'
  const [reglesFamilleOriginales, setReglesFamilleOriginales] = useState(null);
  const [reglesCustom, setReglesCustom] = useState({
    reduction_inscription: '',
    reduction_mensuel: '',
    motif: ''
  });

  // Réduction exceptionnelle
  // Meme regle que pour la politique d'ecole ci-dessus : les montants et le motif
  // ne sont repris que si la reduction exceptionnelle etait deja choisie. Sinon ce
  // seraient ceux d'un autre mode : revenu a cette etape en mode famille, passer
  // par « Aucune » puis « Exceptionnelle » affichait ici les montants de la
  // famille, et la premiere frappe (le motif, par exemple) les recopiait dans
  // l'inscription, envoyes ensuite comme reduction exceptionnelle.
  const exceptionnelleDejaChoisie = Boolean(familleInfo.reduction_exceptionnelle);
  const [reductionExceptionnelle, setReductionExceptionnelle] = useState({
    reduction_inscription: exceptionnelleDejaChoisie ? (familleInfo.reduction_inscription || '') : '',
    reduction_mensuel: exceptionnelleDejaChoisie ? (familleInfo.reduction_mensuel || '') : '',
    motif: exceptionnelleDejaChoisie ? (familleInfo.reduction_motif || '') : ''
  });

  // 🆕 Récupérer les frais de la classe
  const fraisClasse = classeSelectionnee ? {
    inscription: parseFloat(classeSelectionnee.frais_inscription) || 0,
    mensuel: parseFloat(classeSelectionnee.frais_mensuel) || 0,
    classe_nom: classeSelectionnee.nom || 'Classe'
  } : null;

  // La route est paginée à 12 familles par page. Cet appel n'envoyait aucun
  // paramètre : seules les douze premières par ordre alphabétique arrivaient, et
  // la recherche ci-dessous, purement locale, ne pouvait trouver que parmi
  // celles-là. Avec 227 familles actives, la quasi-totalité était inatteignable
  // depuis le parcours d'inscription — « Famille Keifa » entre autres.
  //
  // La recherche interroge donc le serveur, qui sait filtrer sur le nom. Le
  // premier chargement remonte une page large : assez pour choisir sans taper
  // quand l'école a peu de familles, et la recherche prend le relais au-delà.
  const loadFamilles = async (recherche = '') => {
    try {
      setLoading(true);
      const response = await api.familles.getAll({
        limit: 200,
        ...(recherche.trim() ? { search: recherche.trim() } : {})
      });

      if (response.success && Array.isArray(response.data)) {
        setFamilles(response.data);
        setTotalFamilles(Number(response.pagination?.total ?? response.data.length));
      } else {
        console.error('Format de données inattendu:', response);
        setFamilles([]);
        setTotalFamilles(0);
      }
    } catch (error) {
      console.error('Erreur chargement familles:', error);
      setError('Erreur lors du chargement des familles');
      setFamilles([]);
      setTotalFamilles(0);
    } finally {
      setLoading(false);
    }
  };



  // La frappe déclenche une recherche serveur, après une pause : sans elle, on
  // enverrait une requête par caractère.
  useEffect(() => {
    if (typeReduction !== 'famille') return undefined;
    const minuteur = setTimeout(() => { loadFamilles(searchTerm); }, 300);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, typeReduction]);

  // Le serveur a déjà filtré : on n'affine plus localement, sinon une famille
  // trouvée par le serveur mais absente du premier lot disparaîtrait à nouveau.
  useEffect(() => {
    setFilteredFamilles(familles);
    setHighlightedIndex(-1);
  }, [familles]);



  // Fermer le dropdown en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);


  // Gestion du clavier (flèches haut/bas, Enter)
  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredFamilles.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredFamilles[highlightedIndex]) {
          handleFamilleSelectFromSearch(filteredFamilles[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  // Sélectionner une famille depuis la recherche
  const handleFamilleSelectFromSearch = (famille) => {
    setFamilleSelectionnee(famille);
    setSearchTerm(famille.nom);
    setShowDropdown(false);

    // Stocker les règles originales
    setReglesFamilleOriginales({
      reduction_inscription: parseFloat(famille.reduction_inscription) || 0,
      reduction_mensuel: parseFloat(famille.reduction_mensuel) || 0
    });

    // Réinitialiser le choix
    setChoixReglesFamille(null);
    setReglesCustom({
      reduction_inscription: '',
      reduction_mensuel: '',
      motif: ''
    });
  };

  const retourChoixFamille = () => {
    setMode('');
    setFamilleSelectionnee(null);
    setSearchTerm('');
    setShowDropdown(false);
    setChoixReglesFamille(null);
    setError(null);
  };


  // Gestion du choix du type de réduction
  const handleTypeReductionChange = (type) => {
    setTypeReduction(type);
    setMode('');
    setError(null);

    if (type === null) {
      // Aucune réduction
      setFamilleInfo({
        appartient_famille: false,
        famille_id: null,
        famille_nom: '',
        reduction_inscription: 0,
        reduction_mensuel: 0,
        reduction_exceptionnelle: false,
        reduction_motif: '',
        // 🆕 NOUVEAU
        politique_ecole: false,
        politique_type: null,
        politique_motif: ''
      });


      if (onValidate) {
        onValidate();
      }
    } else if (type === 'famille') {
      // Famille nombreuse
      setFamilleInfo(prev => ({
        ...prev,
        appartient_famille: true,
        reduction_exceptionnelle: false,
        reduction_motif: ''
      }));
    } else if (type === 'exceptionnelle') {
      // Réduction exceptionnelle
      setFamilleInfo(prev => ({
        ...prev,
        appartient_famille: false,
        famille_id: null,
        famille_nom: '',
        reduction_exceptionnelle: true
      }));
    }
    else if (type === 'politique') {
      // 🆕 NOUVEAU : Politique d'école
      setFamilleInfo(prev => ({
        ...prev,
        appartient_famille: false,
        famille_id: null,
        famille_nom: '',
        reduction_exceptionnelle: false,
        reduction_motif: '',
        politique_ecole: true
      }));
    }

  };


  // Gestion de la sélection d'une famille existante
  const handleFamilleSelect = (e) => {
    const familleId = parseInt(e.target.value);
    const famille = familles.find(f => f.id === familleId);

    if (famille) {
      setFamilleSelectionnee(famille);
      // 🆕 Stocker les règles originales de la famille
      setReglesFamilleOriginales({
        reduction_inscription: parseFloat(famille.reduction_inscription) || 0,
        reduction_mensuel: parseFloat(famille.reduction_mensuel) || 0
      });
      // 🆕 Réinitialiser le choix
      setChoixReglesFamille(null);
      setReglesCustom({
        reduction_inscription: '',
        reduction_mensuel: '',
        motif: ''
      });
    }
  };


  // 🆕 Gestion du choix de règles (mêmes règles ou custom)
  const handleChoixReglesFamille = (choix) => {
    setChoixReglesFamille(choix);

    if (choix === 'meme') {
      // Appliquer les règles de la famille
      setFamilleInfo({
        appartient_famille: true,
        famille_id: familleSelectionnee.id,
        famille_nom: familleSelectionnee.nom,
        reduction_inscription: reglesFamilleOriginales.reduction_inscription,
        reduction_mensuel: reglesFamilleOriginales.reduction_mensuel,
        reduction_exceptionnelle: false,
        reduction_motif: '',
        utilise_reduction_famille: true // 🆕 Flag important
      });
    } else if (choix === 'custom') {
      // Préparer pour saisie custom
      setFamilleInfo(prev => ({
        ...prev,
        appartient_famille: true,
        famille_id: familleSelectionnee.id,
        famille_nom: familleSelectionnee.nom,
        reduction_inscription: 0,
        reduction_mensuel: 0,
        utilise_reduction_famille: false // 🆕 Flag important
      }));
    } else if (choix === 'aucune') {
      // Rattachement sans réduction. C'est un choix à part entière : une famille
      // dont la valeur de référence vaut 0 ne laissait auparavant que l'option
      // « appliquer les mêmes règles », qui n'appliquait justement rien.
      setFamilleInfo({
        appartient_famille: true,
        famille_id: familleSelectionnee.id,
        famille_nom: familleSelectionnee.nom,
        reduction_inscription: 0,
        reduction_mensuel: 0,
        reduction_exceptionnelle: false,
        reduction_motif: '',
        utilise_reduction_famille: false
      });
      setReglesCustom({
        reduction_inscription: '',
        reduction_mensuel: '',
        motif: ''
      });
    }
  };

  // 🆕 Gestion des règles custom
  const handleReglesCustomChange = (field, value) => {
    const updated = { ...reglesCustom, [field]: value };
    setReglesCustom(updated);

    setFamilleInfo(prev => ({
      ...prev,
      reduction_inscription: parseFloat(updated.reduction_inscription) || 0,
      reduction_mensuel: parseFloat(updated.reduction_mensuel) || 0,
      reduction_motif: updated.motif,
      utilise_reduction_famille: false
    }));
  };



  // 🆕 NOUVEAU : Gestion politique d'école
  const handlePolitiqueEcoleChange = (field, value) => {
    const updated = { ...politiqueEcole, [field]: value };
    setPolitiqueEcole(updated);

    // Calculer automatiquement reduction_inscription = 0 pour tous les types
    let reductionInscription = 0;

    if (updated.type) {
      reductionInscription = fraisClasse?.inscription || 0; // Réduction = montant total
    }

    setFamilleInfo(prev => ({
      ...prev,
      politique_ecole: true,
      politique_type: updated.type,
      reduction_inscription: reductionInscription,
      reduction_mensuel: parseFloat(updated.reduction_mensuel) || 0,
      politique_motif: updated.motif
    }));
  };

  // Gestion de la création d'une nouvelle famille
  const handleCreateFamille = async () => {
    try {
      if (!nouvelleFamille.nom.trim()) {
        setError('Le nom de la famille est requis');
        return;
      }

      const reductionInscription = parseFloat(nouvelleFamille.reduction_inscription) || 0;
      const reductionMensuel = parseFloat(nouvelleFamille.reduction_mensuel) || 0;
      const motifReduction = (nouvelleFamille.motif || '').trim();
      const aUneReduction = reductionInscription > 0 || reductionMensuel > 0;

      // Validation : réduction ne doit pas dépasser le montant
      if (fraisClasse) {
        if (reductionInscription > fraisClasse.inscription) {
          setError(`La réduction inscription ne peut pas dépasser ${formaterMontant(fraisClasse.inscription)} FCFA`);
          return;
        }
        if (reductionMensuel > fraisClasse.mensuel) {
          setError(`La réduction mensuelle ne peut pas dépasser ${formaterMontant(fraisClasse.mensuel)} FCFA`);
          return;
        }
      }

      // La règle « au moins une réduction > 0 » est levée : une famille sans valeur
      // commune est le cas normal, pas une anomalie — deux tiers des familles
      // existantes n'en ont aucune qui serve à quelqu'un.
      if (aUneReduction && !motifReduction) {
        setError('Le motif est obligatoire lorsqu\'une réduction est accordée');
        return;
      }

      setLoading(true);
      setError(null);

      // La réduction saisie est celle de CET élève. Elle ne devient la valeur de la
      // famille — celle que les prochains enfants pourront reprendre — que si la
      // gestionnaire le demande explicitement.
      //
      // Auparavant elle y était versée systématiquement : on fabriquait une
      // politique familiale à partir d'un cas individuel. D'où, en base, des valeurs
      // de famille qui ne sont que le reflet de la réduction d'un membre, et que
      // personne n'utilise dans 56 % des cas.
      const response = await api.familles.create({
        nom: nouvelleFamille.nom.trim(),
        reduction_inscription: nouvelleFamille.valeur_de_famille ? reductionInscription : 0,
        reduction_mensuel: nouvelleFamille.valeur_de_famille ? reductionMensuel : 0,
        description: nouvelleFamille.description.trim()
      });

      if (response.success && response.data) {
        setFamilleInfo({
          appartient_famille: true,
          famille_id: response.data.id,
          famille_nom: response.data.nom,
          // Les montants restent ceux saisis pour l'élève, que la famille les porte
          // ou non. Le drapeau, lui, dit d'où ils viennent : de la valeur de famille
          // si la case a été cochée, sinon d'une réduction qui n'appartient qu'à lui.
          reduction_inscription: reductionInscription,
          reduction_mensuel: reductionMensuel,
          reduction_exceptionnelle: false,
          reduction_motif: motifReduction,
          utilise_reduction_famille: Boolean(nouvelleFamille.valeur_de_famille)
        });

        setNouvelleFamille({
          nom: '',
          reduction_inscription: '',
          reduction_mensuel: '',
          motif: '',
          valeur_de_famille: false,
          description: ''
        });

        await loadFamilles();

        setToastInfo({
          message: `Famille "${response.data.nom}" créée avec succès !`,
          type: 'success'
        });

        setTimeout(() => {
          setMode('select');
        }, 1500);
      }

      else {
        setError(response.message || 'Erreur lors de la création de la famille');
      }
    } catch (error) {
      console.error('Erreur création famille:', error);
      setError('Erreur lors de la création de la famille');

      setToastInfo({
        message: 'Erreur lors de la création de la famille',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Gestion de la réduction exceptionnelle
  const handleReductionExceptionnelleChange = (field, value) => {
    const updated = { ...reductionExceptionnelle, [field]: value };
    setReductionExceptionnelle(updated);

    if (field === 'reduction_inscription' || field === 'reduction_mensuel' || field === 'motif') {
      setFamilleInfo(prev => ({
        ...prev,
        reduction_inscription: parseFloat(updated.reduction_inscription) || 0,
        reduction_mensuel: parseFloat(updated.reduction_mensuel) || 0,
        reduction_motif: updated.motif
      }));
    }
  };

  // 🆕 Calcul en temps réel pour famille nombreuse
  const calculInscriptionFamille = fraisClasse && nouvelleFamille.reduction_inscription
    ? calculerMontantsAvecReduction(
      fraisClasse.inscription,
      parseFloat(nouvelleFamille.reduction_inscription) || 0
    )
    : null;

  const calculMensuelFamille = fraisClasse && nouvelleFamille.reduction_mensuel
    ? calculerMontantsAvecReduction(
      fraisClasse.mensuel,
      parseFloat(nouvelleFamille.reduction_mensuel) || 0
    )
    : null;

  // 🆕 Calcul en temps réel pour réduction exceptionnelle
  const calculInscriptionExceptionnelle = fraisClasse && reductionExceptionnelle.reduction_inscription
    ? calculerMontantsAvecReduction(
      fraisClasse.inscription,
      parseFloat(reductionExceptionnelle.reduction_inscription) || 0
    )
    : null;

  const calculMensuelExceptionnelle = fraisClasse && reductionExceptionnelle.reduction_mensuel
    ? calculerMontantsAvecReduction(
      fraisClasse.mensuel,
      parseFloat(reductionExceptionnelle.reduction_mensuel) || 0
    )
    : null;

  // 🆕 Calcul économie annuelle
  const economieAnnuelle = (typeReduction === 'famille' && calculInscriptionFamille && calculMensuelFamille)
    ? calculerEconomieAnnuelle(
      calculInscriptionFamille.reduction,
      calculMensuelFamille.reduction
    )
    : (typeReduction === 'exceptionnelle' && calculInscriptionExceptionnelle && calculMensuelExceptionnelle)
      ? calculerEconomieAnnuelle(
        calculInscriptionExceptionnelle.reduction,
        calculMensuelExceptionnelle.reduction
      )
      : null;

  // Rendu du formulaire de création
  const renderCreateForm = () => (
    <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
      <button
        type="button"
        onClick={retourChoixFamille}
        className="mb-4 inline-flex items-center gap-2 rounded-md border border-[#b8d4e2] bg-[#e7f3f8] px-3 py-2 text-sm font-medium text-[#246990] hover:bg-[#d9edf5]"
      >
        <ArrowLeft size={16} />
        Retour au choix
      </button>
      <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
        Créer une nouvelle famille nombreuse
      </h3>

      {/* 🆕 AFFICHAGE FRAIS DE LA CLASSE */}
      {fraisClasse && (
        <div className="mb-4 rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Frais officiels de la classe {fraisClasse.classe_nom}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex items-center gap-2">

              <span className="text-sm text-gray-600">Inscription :</span>
              <span className="font-bold text-gray-800">{formaterMontant(fraisClasse.inscription)} FCFA</span>
            </div>
            <div className="flex items-center gap-2">

              <span className="text-sm text-gray-600">Mensuel :</span>
              <span className="font-bold text-gray-800">{formaterMontant(fraisClasse.mensuel)} FCFA</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom de la famille <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={nouvelleFamille.nom}
            onChange={(e) => setNouvelleFamille(prev => ({ ...prev, nom: e.target.value }))}
            placeholder="Ex: Famille KOUASSI"
            className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Réduction sur frais d'inscription
          </label>
          <div className="relative">
            <input
              type="number"
              value={nouvelleFamille.reduction_inscription}
              onChange={(e) => setNouvelleFamille(prev => ({ ...prev, reduction_inscription: e.target.value }))}
              placeholder="0"
              min="0"
              max={fraisClasse?.inscription || 999999}
              step="any"
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pr-14 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">FCFA</span>
          </div>
          {calculInscriptionFamille && (
            <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
              <p className="text-sm text-green-800">
                Nouveau montant : <strong>{formaterMontant(calculInscriptionFamille.montant_final)} FCFA</strong>
                {calculInscriptionFamille.pourcentage > 0 && (
                  <span className="ml-2 text-xs">({calculInscriptionFamille.pourcentage}% de réduction)</span>
                )}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Réduction sur frais mensuels
          </label>
          <div className="relative">
            <input
              type="number"
              value={nouvelleFamille.reduction_mensuel}
              onChange={(e) => setNouvelleFamille(prev => ({ ...prev, reduction_mensuel: e.target.value }))}
              placeholder="0"
              min="0"
              max={fraisClasse?.mensuel || 999999}
              step="any"
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pr-14 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">FCFA</span>
          </div>
          {calculMensuelFamille && (
            <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
              <p className="text-sm text-green-800">
                Nouveau montant : <strong>{formaterMontant(calculMensuelFamille.montant_final)} FCFA</strong>
                {calculMensuelFamille.pourcentage > 0 && (
                  <span className="ml-2 text-xs">({calculMensuelFamille.pourcentage}% de réduction)</span>
                )}
              </p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motif de la réduction
            {(parseFloat(nouvelleFamille.reduction_inscription) > 0 || parseFloat(nouvelleFamille.reduction_mensuel) > 0)
              && <span className="text-red-500"> *</span>}
          </label>
          <input
            type="text"
            value={nouvelleFamille.motif}
            onChange={(e) => setNouvelleFamille(prev => ({ ...prev, motif: e.target.value }))}
            placeholder="Ex : fratrie de 3 enfants, situation familiale, accord de la direction..."
            className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
          />
          <p className="mt-1 text-xs text-gray-500">
            Obligatoire dès qu'une réduction est accordée : sans lui, personne ne pourra
            dire plus tard pourquoi cet élève paie moins que le tarif.
          </p>
        </div>

        {/* Le choix qui n'existait pas.
            La réduction saisie ci-dessus concerne CET élève. Avant, elle était versée
            d'office dans la famille et devenait sa « réduction de référence » — d'où,
            en base, des valeurs de famille qui ne sont que le reflet d'un membre et
            que personne ne reprend dans 56 % des cas. */}
        <div className="lg:col-span-2 rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(nouvelleFamille.valeur_de_famille)}
              onChange={(e) => setNouvelleFamille(prev => ({ ...prev, valeur_de_famille: e.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#246990] focus:ring-[#246990]"
            />
            <span className="text-sm text-gray-700">
              <span className="font-medium text-gray-900">
                Faire de cette réduction la valeur de la famille
              </span>
              <span className="mt-0.5 block text-xs text-gray-600">
                Les prochains enfants ajoutés pourront la reprendre d'un clic. Si vous
                laissez décoché, cette réduction reste propre à cet élève et chaque
                enfant aura la sienne — c'est le cas le plus fréquent.
              </span>
            </span>
          </label>
        </div>

        {economieAnnuelle && (
          <div className="rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3 lg:col-span-2">
            <p className="text-sm font-medium text-blue-900 mb-2">Économie faite par l'élève sur l'année scolaire :</p>
            <div className="grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-3">
              <p>• Inscription : {formaterMontant(economieAnnuelle.economie_inscription)} FCFA</p>
              <p>• 9 mois × {formaterMontant(calculMensuelFamille?.reduction || 0)} = {formaterMontant(economieAnnuelle.economie_mensuelle)} FCFA</p>
              <p className="font-semibold text-[#246990]">
                Total économisé : {formaterMontant(economieAnnuelle.economie_totale)} FCFA
              </p>
            </div>
          </div>
        )}

        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (optionnel)
          </label>
          <textarea
            value={nouvelleFamille.description}
            onChange={(e) => setNouvelleFamille(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Informations supplémentaires..."
            rows={2}
            className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
          />
        </div>

        <div className="flex flex-wrap justify-end gap-3 pt-1 lg:col-span-2">
          <button
            type="button"
            onClick={handleCreateFamille}
            disabled={loading}
            className="rounded-md bg-[#246990] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#3084b5] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {loading ? (
              <>Création en cours...</>
            ) : (
              <>
                Créer la famille
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              retourChoixFamille();
              setNouvelleFamille({ nom: '', reduction_inscription: '', reduction_mensuel: '', description: '' });
              setError(null);
            }}
            className="rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );



  // 🆕 Rendu du choix de règles après sélection de famille
  const renderChoixReglesFamille = () => {
    if (!familleSelectionnee || !reglesFamilleOriginales) return null;

    // Calculs pour affichage
    const calculInscriptionFamilleOriginale = fraisClasse
      ? calculerMontantsAvecReduction(fraisClasse.inscription, reglesFamilleOriginales.reduction_inscription)
      : null;

    const calculMensuelFamilleOriginale = fraisClasse
      ? calculerMontantsAvecReduction(fraisClasse.mensuel, reglesFamilleOriginales.reduction_mensuel)
      : null;

    const calculInscriptionCustom = fraisClasse && reglesCustom.reduction_inscription
      ? calculerMontantsAvecReduction(fraisClasse.inscription, parseFloat(reglesCustom.reduction_inscription) || 0)
      : null;

    const calculMensuelCustom = fraisClasse && reglesCustom.reduction_mensuel
      ? calculerMontantsAvecReduction(fraisClasse.mensuel, parseFloat(reglesCustom.reduction_mensuel) || 0)
      : null;

    // Une famille n'a pas forcément de réduction de référence : c'est une valeur
    // facultative, saisie à la création. Quand elle vaut 0, proposer de « reprendre
    // les mêmes règles » revient à proposer de ne rien appliquer sous un autre nom.
    const familleADesReductions = reglesFamilleOriginales.reduction_inscription > 0
      || reglesFamilleOriginales.reduction_mensuel > 0;

    // Le panneau du dessus affiche des RÉDUCTIONS, ces cartes des MONTANTS À PAYER.
    // Sans le dire, les deux se lisaient comme des grandeurs de même nature et se
    // contredisaient : « réduction inscription 0 » puis « inscription 2 000 ».
    const renderMontantApresReduction = (libelle, calcul) => (
      // En `span` et non en `div`/`p` : ce bloc vit desormais dans le bouton
      // d'une CarteChoix, et un `p` dans un `button` est du HTML invalide.
      <span className="block rounded-md border border-gray-200 bg-white p-2">
        <span className="block text-gray-600">{libelle} à payer</span>
        <span className="block font-semibold text-[#246990]">
          {formaterMontant(calcul?.montant_final || 0)} FCFA
        </span>
        <span className="mt-0.5 block text-[10px] leading-snug text-gray-500">
          {calcul?.reduction > 0
            ? `${formaterMontant(calcul.montant_original)} FCFA de frais moins ${formaterMontant(calcul.reduction)} FCFA de réduction`
            : 'Aucune réduction de famille sur cette ligne'}
        </span>
      </span>
    );

    // Le piège : une réduction propre plus petite que la valeur famille remplace
    // cette dernière, donc fait payer davantage. Il se voit pendant la saisie.
    const customMensuel = parseFloat(reglesCustom.reduction_mensuel) || 0;
    const avertissementEviction = familleADesReductions
      && choixReglesFamille === 'custom'
      && customMensuel > 0
      && customMensuel < reglesFamilleOriginales.reduction_mensuel
      ? {
        valeurFamille: reglesFamilleOriginales.reduction_mensuel,
        personnalisee: customMensuel,
        mensuelSiValeurFamille: calculMensuelFamilleOriginale?.montant_final || 0,
        mensuelSiPersonnalisee: calculMensuelCustom?.montant_final || 0
      }
      : null;

    return (
      <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[#246990]">Famille sélectionnée</p>
            <h3 className="mt-1 text-base font-semibold text-gray-900">
              {familleSelectionnee.nom}
            </h3>
          </div>
          <span className="rounded-md border border-[#b8d4e2] bg-[#e7f3f8] px-2.5 py-1 text-xs font-medium text-[#246990]">
            {familleSelectionnee.nombre_enfants_reel || 0} enfant(s)
          </span>
        </div>

        {/* Valeur famille : la réduction portée par la famille elle-même */}
        <div className="mb-4 rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3">
          <p className="mb-1 text-sm font-semibold text-gray-900">Valeur famille</p>
          {familleADesReductions ? (
            <>
              <p className="mb-3 text-xs text-gray-600">
                Réduction portée par la famille. Chaque enfant peut la reprendre ou non : elle ne
                s'applique pas d'office.
              </p>
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Réduction inscription</span>
                  {reglesFamilleOriginales.reduction_inscription > 0 ? (
                    <p className="mt-1 font-semibold text-[#246990]">{formaterMontant(reglesFamilleOriginales.reduction_inscription)} FCFA</p>
                  ) : (
                    <p className="mt-1 font-semibold text-gray-400">Aucune</p>
                  )}
                </div>
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Réduction mensuelle</span>
                  {reglesFamilleOriginales.reduction_mensuel > 0 ? (
                    <p className="mt-1 font-semibold text-[#246990]">{formaterMontant(reglesFamilleOriginales.reduction_mensuel)} FCFA</p>
                  ) : (
                    <p className="mt-1 font-semibold text-gray-400">Aucune</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-600">
              Cette famille n'a pas de valeur famille. C'est fréquent : elle est
              facultative et beaucoup de familles fonctionnent avec une réduction propre à chaque
              enfant. L'option « Reprendre la valeur famille » n'est donc pas proposée ici.
            </p>
          )}
        </div>

        {/* CHOIX DE LA RÉDUCTION */}
        <div className="space-y-3">
          <p className="font-semibold text-gray-900 mb-3">Quelle réduction appliquer pour cet élève ?</p>

          {/* Option 1 : reprendre la valeur de la famille — seulement si elle existe */}
          {familleADesReductions && (
            <CarteChoix
              actif={choixReglesFamille === 'meme'}
              onClick={() => handleChoixReglesFamille('meme')}
              titre="Reprendre la valeur famille"
              description="L'élève suivra la valeur famille ci-dessus. Si elle change plus tard, son montant suivra."
            >
              {fraisClasse && (
                <span className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  {renderMontantApresReduction('Inscription', calculInscriptionFamilleOriginale)}
                  {renderMontantApresReduction('Mensuel', calculMensuelFamilleOriginale)}
                </span>
              )}
            </CarteChoix>
          )}

          {/* Option 2 : réduction propre à cet élève */}
          <CarteChoix
            actif={choixReglesFamille === 'custom'}
            onClick={() => handleChoixReglesFamille('custom')}
            titre="Définir une réduction propre à cet élève"
            description={familleADesReductions
              ? "Cette réduction tient lieu de réduction pour l'élève : elle prend la place de la valeur famille au lieu de s'y ajouter."
              : "Une réduction valable pour cet élève seul, indépendante des autres enfants de la famille."}
          />

          {/* Option 3 : aucune réduction */}
          <CarteChoix
            actif={choixReglesFamille === 'aucune'}
            onClick={() => handleChoixReglesFamille('aucune')}
            titre="Aucune réduction"
            description="L'élève rejoint la famille et paie le tarif officiel de sa classe."
          />
        </div>

        {/* Formulaire custom si sélectionné */}
        {choixReglesFamille === 'custom' && (
          <div className="mt-4 space-y-4 rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Définissez les réductions spécifiques pour cet élève :
            </p>

            {/* Réduction inscription */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Réduction sur frais d'inscription
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={reglesCustom.reduction_inscription}
                  onChange={(e) => handleReglesCustomChange('reduction_inscription', e.target.value)}
                  placeholder="0"
                  min="0"
                  max={fraisClasse?.inscription || 999999}
                  step="any"
                  className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pr-14 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">FCFA</span>
              </div>
              {calculInscriptionCustom && (
                <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                  Nouveau montant : <strong>{formaterMontant(calculInscriptionCustom.montant_final)} FCFA</strong>
                </div>
              )}
            </div>

            {/* Réduction mensuelle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Réduction sur frais mensuels
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={reglesCustom.reduction_mensuel}
                  onChange={(e) => handleReglesCustomChange('reduction_mensuel', e.target.value)}
                  placeholder="0"
                  min="0"
                  max={fraisClasse?.mensuel || 999999}
                  step="any"
                  className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pr-14 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">FCFA</span>
              </div>
              {calculMensuelCustom && (
                <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                  Nouveau montant : <strong>{formaterMontant(calculMensuelCustom.montant_final)} FCFA</strong>
                </div>
              )}
            </div>

            {/* Alerte d'éviction : deux montants absolus, aucun comparatif */}
            {avertissementEviction && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-900">
                  Cette réduction remplace la valeur famille, elle ne s'y ajoute pas
                </p>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="rounded-md border border-amber-200 bg-white px-3 py-2">
                    <p className="text-[11px] text-gray-500">
                      En reprenant la valeur famille ({formaterMontant(avertissementEviction.valeurFamille)} FCFA)
                    </p>
                    <p className="font-semibold text-gray-900">
                      {formaterMontant(avertissementEviction.mensuelSiValeurFamille)} FCFA / mois
                    </p>
                  </div>
                  <div className="rounded-md border border-amber-300 bg-white px-3 py-2">
                    <p className="text-[11px] text-gray-500">
                      Avec cette réduction ({formaterMontant(avertissementEviction.personnalisee)} FCFA)
                    </p>
                    <p className="font-semibold text-gray-900">
                      {formaterMontant(avertissementEviction.mensuelSiPersonnalisee)} FCFA / mois
                    </p>
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-amber-800">
                  Pour accorder une réduction qui <span className="font-semibold">s'ajoute</span> à
                  celle de la famille, utilisez la réduction exceptionnelle plus bas dans ce formulaire.
                </p>
              </div>
            )}

            {/* Motif */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motif / Justification <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reglesCustom.motif}
                onChange={(e) => handleReglesCustomChange('motif', e.target.value)}
                placeholder="Expliquez pourquoi cette réduction est propre à cet élève..."
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                required
              />
            </div>
          </div>
        )}
      </div>
    );
  };



  // Rendu du sélecteur de famille
  // Rendu du sélecteur de famille avec recherche
  const renderSelectForm = () => (
    <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
      <button
        type="button"
        onClick={retourChoixFamille}
        className="mb-4 inline-flex items-center gap-2 rounded-md border border-[#b8d4e2] bg-[#e7f3f8] px-3 py-2 text-sm font-medium text-[#246990] hover:bg-[#d9edf5]"
      >
        <ArrowLeft size={16} />
        Retour au choix
      </button>

      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Rechercher une famille existante
        </label>

        {/* Dire combien de familles ne sont pas affichées évite de conclure
            qu'une famille n'existe pas parce qu'elle n'est pas sous les yeux. */}
        {totalFamilles > filteredFamilles.length && (
          <p className="mb-2 text-xs text-gray-500">
            {filteredFamilles.length} famille(s) affichée(s) sur {totalFamilles}.
            Tapez un nom pour chercher dans toutes.
          </p>
        )}

        {/* Champ de recherche */}
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder="Tapez le nom d'une famille..."
            className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pl-10 pr-10 transition-all focus:border-transparent focus:ring-2 focus:ring-[#246990]"
          />

          {/* Icône de recherche */}
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Search size={17} />
          </div>
        </div>

        {/* Dropdown avec résultats */}
        {showDropdown && filteredFamilles.length > 0 && (
          <div className="absolute z-30 mt-2 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl">
            {filteredFamilles.map((famille, index) => (
              <div
                key={famille.id}
                onClick={() => handleFamilleSelectFromSearch(famille)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${highlightedIndex === index
                    ? 'bg-[#eef7fb] border-l-4 border-l-[#246990]'
                    : 'hover:bg-gray-50'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{famille.nom}</p>
                    <div className="flex gap-4 mt-1 text-xs text-gray-600">
                      <span>Inscription : -{formaterMontant(famille.reduction_inscription)} FCFA</span>
                      <span>Mensuel : -{formaterMontant(famille.reduction_mensuel)} FCFA</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center rounded-md border border-[#b8d4e2] bg-[#e7f3f8] px-2 py-1 text-xs font-medium text-[#246990]">
                      {famille.nombre_enfants_reel || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Message si aucun résultat */}
        {showDropdown && searchTerm && filteredFamilles.length === 0 && (
          <div className="absolute z-10 mt-2 w-full rounded-lg border border-gray-200 bg-white p-4 text-center shadow-xl">
            <AlertCircle size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-gray-600 font-medium">Aucune famille trouvée</p>
            <p className="text-sm text-gray-500 mt-1">
              Essayez avec un autre nom ou créez une nouvelle famille
            </p>
          </div>
        )}

        {/* Info : nombre total de familles */}
        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
          <Info size={14} />
          {familles.length} famille(s) disponible(s)
        </p>
      </div>

      {/* Affichage du choix de règles après sélection */}
      {familleSelectionnee && renderChoixReglesFamille()}

      {/* Bouton créer nouvelle famille */}
      <div className="flex justify-end border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => {
            setMode('create');
            setSearchTerm('');
            setShowDropdown(false);
            setFamilleSelectionnee(null);
          }}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-[#b8d4e2] bg-[#e7f3f8] px-4 py-2.5 text-sm font-medium text-[#246990] hover:bg-[#d9edf5]"
        >
          Ou créer une nouvelle famille
        </button>
      </div>
    </div>
  );




  // Rendu du formulaire de réduction exceptionnelle
  const renderReductionExceptionnelle = () => (
    <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-4 text-base font-semibold text-gray-900">
        Réduction exceptionnelle
      </h3>

      {/* 🆕 AFFICHAGE FRAIS DE LA CLASSE */}
      {fraisClasse && (
        <div className="mb-4 rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Frais officiels de la classe {fraisClasse.classe_nom}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex items-center gap-2">

              <span className="text-sm text-gray-600">Inscription :</span>
              <span className="font-bold text-gray-800">{formaterMontant(fraisClasse.inscription)} FCFA</span>
            </div>
            <div className="flex items-center gap-2">

              <span className="text-sm text-gray-600">Mensuel :</span>
              <span className="font-bold text-gray-800">{formaterMontant(fraisClasse.mensuel)} FCFA</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Réduction sur frais d'inscription
          </label>
          <div className="relative">
            <input
              type="number"
              value={reductionExceptionnelle.reduction_inscription}
              onChange={(e) => handleReductionExceptionnelleChange('reduction_inscription', e.target.value)}
              placeholder="0"
              min="0"
              max={fraisClasse?.inscription || 999999}
              step="any"
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pr-14 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">FCFA</span>
          </div>
          {calculInscriptionExceptionnelle && (
            <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
              <p className="text-sm text-green-800">
                Nouveau montant : <strong>{formaterMontant(calculInscriptionExceptionnelle.montant_final)} FCFA</strong>
                {calculInscriptionExceptionnelle.pourcentage > 0 && (
                  <span className="ml-2 text-xs">({calculInscriptionExceptionnelle.pourcentage}% de réduction)</span>
                )}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Réduction sur frais mensuels <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              value={reductionExceptionnelle.reduction_mensuel}
              onChange={(e) => handleReductionExceptionnelleChange('reduction_mensuel', e.target.value)}
              placeholder="0"
              min="0"
              max={fraisClasse?.mensuel || 999999}
              step="any"
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pr-14 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
              required
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">FCFA</span>
          </div>
          {calculMensuelExceptionnelle && (
            <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
              <p className="text-sm text-green-800">
                Nouveau montant : <strong>{formaterMontant(calculMensuelExceptionnelle.montant_final)} FCFA</strong>
                {calculMensuelExceptionnelle.pourcentage > 0 && (
                  <span className="ml-2 text-xs">({calculMensuelExceptionnelle.pourcentage}% de réduction)</span>
                )}
              </p>
            </div>
          )}
        </div>

        {economieAnnuelle && (
          <div className="rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3 lg:col-span-2">
            <p className="text-sm font-semibold text-gray-900 mb-2">Économie faite par l'élève sur l'année scolaire</p>
            <div className="grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-3">
              <p>• Inscription : {formaterMontant(economieAnnuelle.economie_inscription)} FCFA</p>
              <p>• 9 mois × {formaterMontant(calculMensuelExceptionnelle?.reduction || 0)} = {formaterMontant(economieAnnuelle.economie_mensuelle)} FCFA</p>
              <p className="font-semibold text-[#246990]">
                Total économisé : {formaterMontant(economieAnnuelle.economie_totale)} FCFA
              </p>
            </div>
          </div>
        )}

        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motif / Justification <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reductionExceptionnelle.motif}
            onChange={(e) => handleReductionExceptionnelleChange('motif', e.target.value)}
            placeholder="Expliquez pourquoi cette réduction est accordée (difficulté financière, situation exceptionnelle, etc.)"
            rows={3}
            className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Cette justification sera enregistrée dans le dossier de l'élève
          </p>
        </div>

        {(reductionExceptionnelle.reduction_inscription > 0 || reductionExceptionnelle.reduction_mensuel > 0) && reductionExceptionnelle.motif && (
          <div className="mt-1 rounded-lg border border-green-200 bg-green-50 p-4 lg:col-span-2">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">
                  Réduction exceptionnelle accordée
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Cette réduction sera appliquée sur tous les frais scolaires
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );


  // 🆕 NOUVEAU : Rendu politique d'école
  const renderPolitiqueEcole = () => {
    const calculMensuelPolitique = fraisClasse && politiqueEcole.reduction_mensuel
      ? calculerMontantsAvecReduction(
        fraisClasse.mensuel,
        parseFloat(politiqueEcole.reduction_mensuel) || 0
      )
      : null;

    const economieAnnuellePolitique = fraisClasse && politiqueEcole.type
      ? calculerEconomieAnnuelle(
        fraisClasse.inscription, // Inscription gratuite = économie totale
        calculMensuelPolitique?.reduction || 0
      )
      : null;

    return (
      <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-4 text-base font-semibold text-gray-900">
          Politique d'école
        </h3>

        {/* 🆕 AFFICHAGE FRAIS DE LA CLASSE */}
        {fraisClasse && (
          <div className="mb-4 rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Frais officiels de la classe {fraisClasse.classe_nom}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Inscription :</span>
                <span className="font-bold text-gray-800">{formaterMontant(fraisClasse.inscription)} FCFA</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Mensuel :</span>
                <span className="font-bold text-gray-800">{formaterMontant(fraisClasse.mensuel)} FCFA</span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Sélection du type de politique */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Catégorie <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 gap-3">
              {/* Admis aux examens */}
              <CarteChoix
                actif={politiqueEcole.type === 'admis_examen'}
                onClick={() => handlePolitiqueEcoleChange('type', 'admis_examen')}
                titre="Admis aux examens"
                description="L'élève a réussi les examens d'admission"
              >
                {politiqueEcole.type === 'admis_examen' && fraisClasse && (
                  <span className="mt-2 block rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
                    Frais d'inscription : 0 FCFA (gratuit)
                  </span>
                )}
              </CarteChoix>

              {/* Premier de classe */}
              <CarteChoix
                actif={politiqueEcole.type === 'premier_classe'}
                onClick={() => handlePolitiqueEcoleChange('type', 'premier_classe')}
                titre="Premier de classe"
                description="L'élève était premier de sa classe l'année précédente"
              >
                {politiqueEcole.type === 'premier_classe' && fraisClasse && (
                  <span className="mt-2 block rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
                    Frais d'inscription : 0 FCFA (gratuit)
                  </span>
                )}
              </CarteChoix>

              {/* 100 premiers inscrits */}
              <CarteChoix
                actif={politiqueEcole.type === 'premiers_inscrits'}
                onClick={() => handlePolitiqueEcoleChange('type', 'premiers_inscrits')}
                titre="Parmi les 100 premiers inscrits"
                description="L'élève fait partie des 100 premières inscriptions"
              >
                {politiqueEcole.type === 'premiers_inscrits' && fraisClasse && (
                  <span className="mt-2 block rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
                    Frais d'inscription : 0 FCFA (gratuit)
                  </span>
                )}
              </CarteChoix>
            </div>
          </div>

          {/* Réduction mensuelle optionnelle */}
          {politiqueEcole.type && (
            <>
              <div className="border-t border-gray-100 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Réduction sur frais mensuels (optionnel)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={politiqueEcole.reduction_mensuel}
                    onChange={(e) => handlePolitiqueEcoleChange('reduction_mensuel', e.target.value)}
                    placeholder="0"
                    min="0"
                    max={fraisClasse?.mensuel || 999999}
                    step="any"
                    className="w-full rounded-lg border border-gray-200 px-4 py-2.5 pr-14 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">FCFA</span>
                </div>
                {calculMensuelPolitique && (
                  <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800">
                      Nouveau montant : <strong>{formaterMontant(calculMensuelPolitique.montant_final)} FCFA</strong>
                      {calculMensuelPolitique.pourcentage > 0 && (
                        <span className="ml-2 text-xs">({calculMensuelPolitique.pourcentage}% de réduction)</span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Économie annuelle */}
              {economieAnnuellePolitique && (
                <div className="rounded-lg border border-[#d7e8f0] bg-[#f4fafc] p-3">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Économie totale sur l'année scolaire</p>
                  <div className="grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-3">
                    <p>• Inscription gratuite : {formaterMontant(fraisClasse.inscription)} FCFA</p>
                    {politiqueEcole.reduction_mensuel && (
                      <p>• 9 mois × {formaterMontant(calculMensuelPolitique?.reduction || 0)} = {formaterMontant(economieAnnuellePolitique.economie_mensuelle)} FCFA</p>
                    )}
                    <p className="font-semibold text-[#246990]">
                      Total économisé : {formaterMontant(economieAnnuellePolitique.economie_totale)} FCFA
                    </p>
                  </div>
                </div>
              )}

              {/* Motif obligatoire */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motif / Justification <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={politiqueEcole.motif}
                  onChange={(e) => handlePolitiqueEcoleChange('motif', e.target.value)}
                  placeholder="Expliquez pourquoi cette politique s'applique (ex: Admis avec mention, Rang de classement, Numéro d'inscription, etc.)"
                  rows={3}
                  className="w-full rounded-lg border border-gray-200 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Cette justification sera enregistrée dans le dossier de l'élève
                </p>
              </div>

              {/* Confirmation */}
              {politiqueEcole.type && politiqueEcole.motif && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
                  <div className="flex items-start gap-3">
                    <Info size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800">
                        Politique d'école appliquée
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        Frais d'inscription gratuits + réduction mensuelle {politiqueEcole.reduction_mensuel ? 'appliquée' : 'non applicable'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="rounded-lg border border-[#d7e8f0] bg-[#f4fafc] px-4 py-3">
          <div className="mb-1">
            <h2 className="text-lg font-semibold text-gray-900">
              Réductions sur frais scolaires
            </h2>
          </div>
          <p className="text-gray-600 text-sm">
            Choisissez le type de réduction applicable pour cet élève (une seule option possible).
          </p>
        </div>

        {/* Choix du type de réduction */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-3">
            Type de réduction
          </label>

          {/* Aucun type de réduction disponible : plutôt qu'un unique bouton
              « Aucune » — qui n'est pas un choix — on dit pourquoi. Les
              colonnes de réduction restent en base et se remplissent depuis les
              écrans de comptabilité le jour où l'école y accède. */}
          {!auMoinsUneReduction && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              <p className="font-medium text-gray-800 mb-1">Aucune réduction applicable</p>
              <p className="leading-relaxed">
                Les réductions sur les frais — familles nombreuses, réduction
                exceptionnelle, politique d'école — relèvent des modules de gestion
                financière, non inclus dans la licence de l'établissement. L'élève est
                inscrit au tarif standard de sa classe.
              </p>
            </div>
          )}

          <div
            className={`grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 ${auMoinsUneReduction ? '' : 'hidden'}`}
          >
            <button
              type="button"
              onClick={() => handleTypeReductionChange(null)}
              className={`rounded-lg border px-4 py-3 text-sm font-medium transition-all ${typeReduction === null
                  ? 'border-gray-600 bg-gray-50 text-gray-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
            >
              Aucune
            </button>

            {famillesOuvertes && (
            <button
              type="button"
              onClick={() => handleTypeReductionChange('famille')}
              disabled={typeReduction === 'exceptionnelle' || typeReduction === 'politique'}
              className={`rounded-lg border px-4 py-3 text-sm font-medium transition-all ${typeReduction === 'famille'
                  ? 'border-[#246990] bg-[#f4fafc] text-[#246990]'
                  : (typeReduction === 'exceptionnelle' || typeReduction === 'politique')
                    ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
            >
              Famille nombreuse
            </button>
            )}

            {reductionsFinancieres && (
            <button
              type="button"
              onClick={() => handleTypeReductionChange('exceptionnelle')}
              disabled={typeReduction === 'famille' || typeReduction === 'politique'}
              className={`rounded-lg border px-4 py-3 text-sm font-medium transition-all ${typeReduction === 'exceptionnelle'
                  ? 'border-[#246990] bg-[#f4fafc] text-[#246990]'
                  : (typeReduction === 'famille' || typeReduction === 'politique')
                    ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
            >
              Exceptionnelle
            </button>
            )}

            {/* 🆕 NOUVEAU : Bouton Politique d'école */}
            {reductionsFinancieres && (
            <button
              type="button"
              onClick={() => handleTypeReductionChange('politique')}
              disabled={typeReduction === 'famille' || typeReduction === 'exceptionnelle'}
              className={`rounded-lg border px-4 py-3 text-sm font-medium transition-all ${typeReduction === 'politique'
                  ? 'border-[#246990] bg-[#f4fafc] text-[#246990]'
                  : (typeReduction === 'famille' || typeReduction === 'exceptionnelle')
                    ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
            >
              Politique d'école
            </button>
            )}
          </div>



        </div>

        {/* Contenu selon le type sélectionné */}
        {typeReduction === 'famille' && (
          <>
            {!mode && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setMode('select')}
                  className="rounded-lg border border-gray-200 bg-white p-4 text-left transition-all hover:border-[#b8d4e2] hover:bg-[#f4fafc]"
                >
                  <p className="font-medium text-gray-800">Sélectionner une famille existante</p>
                  <p className="text-xs text-gray-500 mt-1">{familles.length} famille(s) disponible(s)</p>
                </button>

                <button
                  type="button"
                  onClick={() => setMode('create')}
                  className="rounded-lg border border-gray-200 bg-white p-4 text-left transition-all hover:border-[#b8d4e2] hover:bg-[#f4fafc]"
                >
                  <p className="font-medium text-gray-800">Créer une nouvelle famille</p>
                  <p className="text-xs text-gray-500 mt-1">Première inscription</p>
                </button>
              </div>
            )}

            {mode === 'select' && renderSelectForm()}
            {mode === 'create' && renderCreateForm()}

            {loading && !mode && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-sm text-gray-600">Chargement des familles...</p>
              </div>
            )}
          </>
        )}

        {typeReduction === 'exceptionnelle' && renderReductionExceptionnelle()}
        {typeReduction === 'politique' && renderPolitiqueEcole()}

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Toast de notification */}
      {toastInfo && (
        <Toast
          message={toastInfo.message}
          type={toastInfo.type}
          onClose={() => setToastInfo(null)}
        />
      )}
    </>
  );
};

export default FamilleNombreuseStep;
